import { 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Grid,
  Paper,
  Stack,
  alpha,
  useTheme,
  Button
} from "@mui/material";
import { useEffect, useState } from "react";
import ApiFetch from "../../services/api.fetch";
import { Profile } from "../ScreeningProfiles/types";
import { Document, LinkedProfile } from './types';
import { formatCurrency } from "../../utils/currency";
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import CreateProfileModal from "../ScreeningProfiles/CreateProfileModal";
import ProfileAccordion from './ProfileAccordion';
import UnlinkConfirmationModal from './UnlinkConfirmationModal';
import DocumentModal from './DocumentModal';
import { useTranslation } from 'react-i18next';
import { checkProfileCompleteness } from "../../utils/profileUtils";

interface ProfileManagementProps {
  candidateId: string | undefined;
  tenantId: number | undefined;
  setAllLinkedProfilesComplete: (complete: boolean) => void;
  setHasRenterProfile: (hasRenter: boolean) => void;
}

interface FullProfile {
  profile: Profile;
  documents: Document[];
}

const ProfileManagement = ({ candidateId, tenantId, setAllLinkedProfilesComplete, setHasRenterProfile }: ProfileManagementProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState<FullProfile[]>([]);
  const [linkedProfiles, setLinkedProfiles] = useState<LinkedProfile[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [createProfileModalOpen, setCreateProfileModalOpen] = useState(false);
  const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
  const [profileToUnlink, setProfileToUnlink] = useState<LinkedProfile | null>(null);

  const updateLinkedProfiles = (profilesData: FullProfile[]) => {
    if (!candidateId) return;
    ApiFetch.httpGet(`/v1/tenant/profile/candidateProfiles/${candidateId}`)
      .then((response) => {
        const profilesWithData = response.map((profile: LinkedProfile) => {
          const fullProfile = profilesData.find(p => p.profile.id === profile.profileId);
          return {
            ...profile,
            profile: fullProfile?.profile,
            documents: fullProfile?.documents
          };
        });
        setLinkedProfiles(profilesWithData);
        
        // Check if there's at least one profile with screeningType === 'RENTER'
        const hasRenter = profilesWithData.some((profile: LinkedProfile) => 
          profile.profile && profile.profile.screeningType === 'RENTER'
        );
        setHasRenterProfile(hasRenter);
        
        // Check if all profiles are complete
        const allProfilesComplete = profilesWithData.every((profile: LinkedProfile) => {
          if (!profile.profile || !profile.documents) return false;
          const { isComplete } = checkProfileCompleteness(profile.profile, profile.documents);
          return isComplete;
        });
        setAllLinkedProfilesComplete(allProfilesComplete);
      })
      .catch((error) => {
        console.error('Failed to fetch linked profiles:', error);
      });
  };

  useEffect(() => {
    if (tenantId) {
      setLoadingProfiles(true);
      ApiFetch.httpGet(`/v1/tenant/profile/profilesByTenantId/${tenantId}`)
        .then((response) => {
          setProfiles(response);
          if (candidateId) {
            updateLinkedProfiles(response);
          }
        })
        .catch((error) => {
          console.error('Failed to fetch profiles:', error);
        })
        .finally(() => {
          setLoadingProfiles(false);
        });
    }
  }, [tenantId, candidateId]);

  const handleProfileLink = async (profileId: number) => {
    if (!candidateId) return;
    try {
      await ApiFetch.httpPost('/v1/tenant/profile/createCandidateProfile', {
        candidateId: parseInt(candidateId),
        profileId
      });
      updateLinkedProfiles(profiles);
    } catch (error) {
      console.error('Failed to link profile:', error);
    }
  };

  const handleUnlinkClick = (profile: LinkedProfile) => {
    setProfileToUnlink(profile);
    setUnlinkModalOpen(true);
  };

  const handleUnlinkConfirm = async () => {
    if (!profileToUnlink) return;
    try {
      await ApiFetch.httpDelete(`/v1/tenant/profile/deleteCandidateProfile/${profileToUnlink.id}`, null);
      updateLinkedProfiles(profiles);
      setUnlinkModalOpen(false);
      setProfileToUnlink(null);
    } catch (error) {
      console.error('Failed to unlink profile:', error);
    }
  };

  const handleCreateProfileSuccess = (profileId: number) => {
    window.location.href = `/screening-profile/${profileId}`;
  };

  const handleMakeMainRenter = async (profileId: number) => {
    if (!candidateId) return;

    try {
      // Get all renter profiles
      const renterProfiles = linkedProfiles.filter(p => p.profile?.screeningType === 'RENTER');
      
      // Find the profile to make main
      const mainProfile = renterProfiles.find(p => p.id === profileId);
      if (!mainProfile) return;

      // Create new order with the selected profile first
      const newOrder = [
        mainProfile,
        ...renterProfiles.filter(p => p.id !== profileId)
      ];

      // Create the payload for the API
      const orderPayload = newOrder.map((item, index) => ({
        id: item.id,
        candidateId: parseInt(candidateId),
        orderNr: index
      }));

      // Update the order in the backend
      await ApiFetch.httpPost('/v1/property/updateCandidateProfileOrder', orderPayload);

      // Update the local state
      const updatedProfiles = [
        ...newOrder,
        ...linkedProfiles.filter(p => p.profile?.screeningType !== 'RENTER')
      ];
      setLinkedProfiles(updatedProfiles);
    } catch (error) {
      console.error('Failed to update profile order:', error);
    }
  };

  const handleEditClick = (profileId: number) => {
    window.location.href = `/screening-profile/${profileId}`;
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, ml: 2 }}>
              {t('screening.profile_management.section_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2, mb: 2 }}>
              {t('screening.profile_management.section_description')}
            </Typography>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <PersonIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.primary" fontWeight={600}>
                      {t('screening_profile.main_renter')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('screening.profile_management.main_renter_info')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`
                }}>
                  <PersonIcon fontSize="small" sx={{ color: 'secondary.main' }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.primary" fontWeight={600}>
                      {t('screening_profile.guarantor')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('screening.profile_management.guarantor_info')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <FormControl size="small" sx={{ 
              width: "17rem",
              mt: 1,
            }}>
              <InputLabel id="profile-select-label" sx={{
                fontSize: ".8rem",
                marginTop: "-0.1rem",
                fontWeight: "bold",
                color: "#fff",
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&.Mui-focused': {
                  color: "#fff",
                }
              }} shrink={false}>
                <AddIcon sx={{ color: "#fff", mt: "-0.1rem" }} />
                {t('screening.profile_management.add_profile')}
              </InputLabel>
              <Select
                value=""
                onChange={(e) => {
                  if (e.target.value === "Add") {
                    setCreateProfileModalOpen(true);
                  } else {
                    handleProfileLink(Number(e.target.value));
                  }
                }}
                labelId="profile-select-label"
                IconComponent={() => null}
                sx={{
                  userSelect: 'none',
                  boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px",
                  backgroundColor: theme.palette.secondary.main,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary.main,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary.main,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary.main,
                  },
                  '& .MuiSelect-select': {
                    padding: '8px 16px',
                    display: 'flex',
                    gap: 1
                  }
                }}
              >
                <MenuItem key={"Add"} value={"Add"}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <AddIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography fontWeight={500}>
                      Add new profile
                    </Typography>
                  </Stack>
                </MenuItem>
                {profiles
                  .filter((profile) => !linkedProfiles.some((linkedProfile) => linkedProfile.profileId === profile?.profile?.id))
                  .sort((a, b) => {
                    if (a.profile.screeningType === 'RENTER' && b.profile.screeningType !== 'RENTER') return -1;
                    if (a.profile.screeningType !== 'RENTER' && b.profile.screeningType === 'RENTER') return 1;
                    return a.profile.firstname.localeCompare(b.profile.firstname);
                  })
                  .map((profile, index) => (
                    <MenuItem key={profile.profile.id} value={profile.profile.id}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography>
                          {t(`screening_profile.${profile.profile.screeningType.toLowerCase()}`)} - {profile.profile.firstname} {profile.profile.surname}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {(linkedProfiles?.length === 0 && !loadingProfiles) ? (
              <Grid item xs={12}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3,
                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: alpha(theme.palette.warning.main, 0.2),
                      color: 'warning.main'
                    }}
                  >
                    <PersonIcon />
                  </Box>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1" color="warning.main" fontWeight={600}>
                      {t('screening.profile_management.no_profiles')}
                    </Typography>
                    <Typography variant="body2" color="warning.dark">
                      {t('screening.profile_management.no_profiles_description')}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            ) : (
              <Grid item xs={12}>
                {linkedProfiles
                  .filter(profile => profile.profile?.screeningType === 'RENTER')
                  .map((profile, index) => (
                    <ProfileAccordion
                      key={profile.id}
                      profile={profile}
                      index={index}
                      onEdit={handleEditClick}
                      onUnlink={handleUnlinkClick}
                      onMakeMainRenter={handleMakeMainRenter}
                      onDocumentClick={setSelectedDocument}
                    />
                  ))}
                {linkedProfiles
                  .filter(profile => profile.profile?.screeningType === 'GUARANTOR')
                  .map((profile, index) => (
                    <ProfileAccordion
                      key={profile.id}
                      profile={profile}
                      index={index}
                      onEdit={handleEditClick}
                      onUnlink={handleUnlinkClick}
                      onMakeMainRenter={handleMakeMainRenter}
                      onDocumentClick={setSelectedDocument}
                    />
                  ))}
              </Grid>
            )}
          </Stack>
        </Grid>
      </Grid>
      <DocumentModal 
        open={!!selectedDocument} 
        onClose={() => setSelectedDocument(null)} 
        document={selectedDocument} 
      />
      <CreateProfileModal
        open={createProfileModalOpen}
        onClose={() => setCreateProfileModalOpen(false)}
        onSuccess={handleCreateProfileSuccess}
        candidateId={candidateId}
      />
      <UnlinkConfirmationModal
        open={unlinkModalOpen}
        onClose={() => setUnlinkModalOpen(false)}
        onConfirm={handleUnlinkConfirm}
        profile={profileToUnlink}
      />
    </>
  );
};

export default ProfileManagement; 